import { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  Legend,
  defaults,
  LinearScale,
  CategoryScale,
  PointElement,
  LineElement,
  Filler,
} from 'chart.js';
import { useAtom } from 'jotai';
import { atomWithDefault } from 'jotai/utils';
import { fromUnixTime } from 'date-fns';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { capitalize, Button } from '@crazyegginc/hatch';

import { usePermissions, useSelection, useTimeZonedFunctions, useChartTooltip } from '/src/hooks';
import { MouseLine } from '/src/chartjs-plugins/MouseLine';
import { ChartTooltipArrow } from '/src/components/ChartTooltipArrow';
import { SkeletonChart } from '/src/components/SkeletonChart';
import { AppLink } from '/src/features/_global/components/AppLink';
import { abTestDistributionQuery } from '../queries';
import { formatCurrency } from '/src/utils';
import { getDistributionValueCurrency, valueDisplayWorth } from '../value-utils';
import { getAbTestVariantColor } from '../variant-colors';
import { FEATURES, ROOT_ELEMENT_ID } from '/src/features/_global/constants';
import { round } from '/src/utils/math';

defaults.font.family = 'Proxima Nova, Lucida Grande, Tahoma, Verdana, Arial, sans-serif';

ChartJS.register(MouseLine, Legend, LinearScale, CategoryScale, PointElement, LineElement, Filler);

const initialView = 'conversions';

export const abTestDistributionDatasetAtom = atomWithDefault(() => {
  const params = new URLSearchParams(window.location.search);
  return params.get('view') || initialView;
});

function getDatasetKey(dataset) {
  switch (dataset) {
    case 'conversions':
      return 'totalConversions';
    case 'conversion_rate':
      return 'avgConversionRate';
    case 'visitors':
      return 'totalVisitors';
    case 'value':
      return 'totalValue';
  }
}

function getYAxisLabel(dataset, currency) {
  if (dataset === 'value') {
    return `Value (${currency})`;
  }
  if (dataset === 'conversion_rate') {
    return 'Conversion Rate (%)';
  }
  return capitalize(dataset.replace('_', ' '));
}

export function DistributionChart({ abTest, goal, startDate, endDate, timeUnit }) {
  const { currentSelection: selectedVariants } = useSelection();
  const { formatChartLabel, formatChartTooltip, isBefore, startOfDay } = useTimeZonedFunctions();
  const { id } = useParams();
  const { tooltip, external } = useChartTooltip();
  const [dataset] = useAtom(abTestDistributionDatasetAtom);
  const permissions = usePermissions();
  const canEditGoals = permissions.can('edit', FEATURES.GOALS).allowed;

  const { data, error, refetch, isRefetching } = useQuery({
    ...abTestDistributionQuery({
      id: Number(id),
      goalId: goal?.id ? Number(goal.id) : null,
      startDate,
      endDate,
      timeUnit,
    }),
    enabled: !!id && !!goal?.id && Number.isFinite(startDate) && Number.isFinite(endDate) && !!timeUnit,
    keepPreviousData: true,
    cacheTime: 0,
    networkMode: 'always',
  });

  const distributionBuckets = useMemo(() => data?.abTestDistribution ?? [], [data?.abTestDistribution]);
  const valueCurrency = useMemo(() => {
    return getDistributionValueCurrency(abTest, distributionBuckets);
  }, [abTest, distributionBuckets]);

  const chartLabels = useMemo(() => {
    return distributionBuckets.map((bucket) => formatChartLabel(bucket.startDate, timeUnit));
  }, [distributionBuckets, formatChartLabel, timeUnit]);

  const distributionData = useMemo(() => {
    if (!goal) {
      return {
        labels: chartLabels,
        datasets: [],
      };
    }

    const datasetMapping = getDatasetKey(dataset);
    const isValueDataset = dataset === 'value';
    const dataStartDate = goal?.createdAt ? startOfDay(fromUnixTime(goal.createdAt)) : null;

    return {
      labels: chartLabels,
      datasets: selectedVariants.map((variant) => {
        const variantColor = getAbTestVariantColor(variant);

        return {
          label: capitalize(variant.variantName),
          data: distributionBuckets.map((bucket) => {
            const bucketEnd = fromUnixTime(bucket.endDate);
            const dataPoint = bucket?.distribution?.find?.((d) => d.variantId === variant.id)?.[datasetMapping];

            if (isValueDataset) {
              if (dataPoint?.displayWorth != null) {
                return dataPoint.displayWorth / 10000;
              }

              return dataStartDate ? (isBefore(bucketEnd, dataStartDate) ? null : 0) : 0;
            }

            return dataPoint ?? (dataStartDate ? (isBefore(bucketEnd, dataStartDate) ? null : 0) : 0);
          }),
          fill: true,
          backgroundColor: (ctx) => {
            const chart = ctx.chart;
            const { ctx: canvasCtx, chartArea } = chart;
            if (!chartArea) {
              // initial render
              return null;
            }
            const gradient = canvasCtx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
            gradient.addColorStop(0, `${variantColor.hex}1A`);
            gradient.addColorStop(1, `${variantColor.hex}00`);
            return gradient;
          },
          pointRadius: 0,
          pointHoverRadius: 5,
          pointBorderWidth: 2,
          pointBorderColor: 'rgba(255,255,255,0)',
          pointHoverBorderColor: 'rgb(255,255,255)',
          pointStyle: 'circle',
          pointBackgroundColor: variantColor.hex,
          borderColor: variantColor.hex,
        };
      }),
    };
  }, [chartLabels, dataset, distributionBuckets, goal, isBefore, selectedVariants, startOfDay]);

  const options = useMemo(
    () => ({
      responsive: true,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      maintainAspectRatio: false,
      spanGaps: true,
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            color: 'rgb(104, 118, 141)',
            maxTicksLimit: 5,
            padding: 10,
          },
          grid: {
            color: 'rgba(151, 151, 151, 0.2)',
            drawTicks: false,
          },
          border: {
            display: false,
          },
          grace: 0,
        },
        x: {
          ticks: {
            color: 'rgb(104, 118, 141)',
            padding: 15,
            callback: function (value) {
              return chartLabels[value];
            },
            align: 'inner',
            autoSkip: false,
          },
          grid: {
            display: false,
          },
          border: {
            display: false,
          },
        },
      },
      plugins: {
        tooltip: {
          enabled: false,
          external,
        },
        legend: {
          labels: false,
        },
        mouseline: {
          enabled: true,
        },
      },
    }),
    [external, chartLabels],
  );

  if (!abTest || !goal) return null;

  return (
    <div className="w-full">
      <div
        className="flex w-full flex-col rounded border border-mystic-500 bg-white rounded-br-none rounded-bl-none"
        style={{
          boxShadow: '0 4px 6px 0 rgba(7,31,49,0.07)',
        }}
      >
        {error ? (
          <div className="flex flex-row p-8">
            <div className="flex h-[300px] w-full flex-col items-center justify-center space-y-3">
              <span className="text-body-5">Couldn&apos;t load the distribution chart for this selection.</span>
              <Button onClick={() => refetch?.()} variant="ghost-primary">
                Retry chart
              </Button>
            </div>
          </div>
        ) : dataset === 'value' && !(abTest.selectedGoal?.hasValue || valueDisplayWorth(abTest.totalValue) > 0) ? (
          <div className="flex flex-row p-8">
            <div className="flex h-[300px] w-full flex-col items-center justify-center">
              <span className="text-body-5">A value for the Conversion Event has not been set or detected.</span>
              {canEditGoals ? (
                <AppLink to={`/goals/${goal.id}/edit-value`} className="text-link mt-2">
                  Set Conversion value
                </AppLink>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="flex flex-row p-8">
            <div className="flex h-[300px] w-5 items-center justify-center">
              <span className="text-caption -rotate-90 transform whitespace-nowrap">
                {getYAxisLabel(dataset, valueCurrency)}
              </span>
            </div>
            <div className="relative flex h-[300px] min-w-[500px] flex-1">
              {isRefetching ? <SkeletonChart /> : <Line data={distributionData} options={options} />}
              {renderChartTooltip({
                tooltip,
                label: distributionBuckets[tooltip?.dataIndex]
                  ? formatChartTooltip(
                      distributionBuckets[tooltip.dataIndex].startDate,
                      distributionBuckets[tooltip.dataIndex].endDate,
                      timeUnit,
                    )
                  : '',
                valueFormatter: getValueFormatter(dataset, valueCurrency),
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function renderChartTooltip({ tooltip, label, valueFormatter }) {
  if (!tooltip || !tooltip.visible) return null;
  if (typeof document === 'undefined') return null;
  const root = document.querySelector(`#${ROOT_ELEMENT_ID}`);
  const portalOffset = getPortalOffset(tooltip);
  const content = (
    <ChartTooltip tooltip={tooltip} label={label} valueFormatter={valueFormatter} positionOffset={portalOffset} />
  );

  return root ? createPortal(content, root) : content;
}

function getPortalOffset(tooltip) {
  if (typeof window === 'undefined') return { x: 0, y: 0 };
  const left = tooltip?.canvasLeft ?? 0;
  const top = tooltip?.canvasTop ?? 0;
  return {
    x: left + window.scrollX,
    y: top + window.scrollY,
  };
}

function ChartTooltip({ tooltip, label, valueFormatter, positionOffset = { x: 0, y: 0 } }) {
  const { anchorX, anchorY, attachSide, items = [], transform, offsetXMultiplier, offsetYMultiplier } = tooltip;

  const GAP = 10; // distance from tooltip to chart point

  let offsetX = offsetXMultiplier * GAP;
  let offsetY = offsetYMultiplier * GAP;

  return (
    <div
      role="tooltip"
      className="absolute pointer-events-none w-[150px] bg-woodsmoke-500/90 rounded text-white p-2.5 text-sm"
      style={{
        left: positionOffset.x + anchorX + offsetX,
        top: positionOffset.y + anchorY + offsetY,
        transform,
      }}
    >
      {label && (
        <div className="font-semibold mb-1.25" data-testid="time-chart-tooltip-date">
          {label}
        </div>
      )}

      <div>
        {items?.map((it, idx) => (
          <div key={`${it.datasetIndex}-${it.dataIndex}-${idx}`} className="flex justify-between items-center">
            <div className="flex items-center min-w-0 space-x-2.5">
              <span
                aria-hidden
                className="inline-block w-2 h-2 rounded-full shrink-0"
                style={{
                  background: it.color,
                }}
              />
              <span className="truncate" data-testid="time-chart-tooltip-label">
                {it.datasetLabel}
              </span>
            </div>

            <div className="font-semibold" data-testid="time-chart-tooltip-value">
              {valueFormatter(it.raw)}
            </div>
          </div>
        ))}
      </div>

      <ChartTooltipArrow side={attachSide} />
    </div>
  );
}

function getValueFormatter(dataset, currency) {
  if (dataset === 'conversion_rate') {
    return (value) => (value == null ? '' : `${round(value, 1)}%`);
  }
  if (dataset === 'value') {
    return (value) => (value == null ? '' : formatCurrency(Number(value), currency));
  }
  return (value) => {
    if (value == null) return '';
    const numeric = Number(value);
    if (Number.isNaN(numeric)) return '';
    return numeric.toLocaleString();
  };
}
